"use client";

import React, {
  createContext,
  useContext,
  useReducer,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";

import { cohesion } from "@redventures/cohesion-utils-react";
import { callMonarchApi } from "@/lib/cohesion/monarchApi";
import { useMonarchLocale } from "@/lib/monarch/useMonarchLocale";
import { withMonarchTranslations } from "@/lib/monarch/resolveMonarchTranslations";
import { FEATURE_FLAG_QUERY_PREFIX } from "@/lib/feature-flags/constants";
import { getEnv } from "@/env";

interface RuleState<T = unknown> {
  isLoading: boolean;
  value: T;
}

type State = Record<string, RuleState>;

interface RequestSuccessAction {
  type: "REQUEST_SUCCESS";
  key: string;
  value: unknown;
}

interface RequestFailureAction {
  type: "REQUEST_FAILURE";
  key: string;
}

type Action = RequestSuccessAction | RequestFailureAction;

// Define types for the context
interface MonarchRuleContextType {
  state: State;
  fetchRule: (
    ruleName: string,
    params: Record<string, unknown> | undefined,
  ) => void;
  hasMounted: boolean;
  monarchDisabledByQuery: boolean;
}

const MonarchRuleContext = createContext<MonarchRuleContextType | undefined>(
  undefined,
);

// OneTrust analytics/performance cookie category. Mirrors
// `consent.cookieClassification` in the Cohesion config and the same check in
// CohesionReady. Monarch only records a MonarchEvaluated exposure when the
// evaluation carries this consent.
const ANALYTICS_CONSENT_GROUP = "C0002";

// Gates the consent-driven re-evaluation below. Off means a rule is evaluated
// once and never replayed, so a consenting user's MonarchEvaluated exposure is
// only recorded on their next page load.
const CONSENT_REEVALUATION_FLAG =
  "NEXT_PUBLIC_FEATURE_MONARCH_CONSENT_REEVALUATION";

// True when Cohesion will report analytics consent on the next evaluation. Reads
// the same source Cohesion uses to build the outgoing evaluation payload
// (`window._Cohesion.webContext.cohesionConsent`), so it reflects exactly what
// the `/evaluations` request will send, with OneTrust active groups as a
// fallback.
const hasAnalyticsConsent = (): boolean => {
  if (typeof window === "undefined") {
    return false;
  }
  if (window._Cohesion?.webContext?.cohesionConsent === true) {
    return true;
  }
  // OnetrustActiveGroups is a comma-wrapped string (e.g. ",C0001,C0002,").
  // Match on the wrapped group id so "C0002" can't be a substring false
  // positive (e.g. "C00021"), mirroring the check in the root layout.
  const activeGroups = window.OnetrustActiveGroups ?? "";
  return activeGroups.includes(`,${ANALYTICS_CONSENT_GROUP},`);
};

const getBrowserUrl = (): URL | null => {
  try {
    const href = window.location?.href;
    if (!href) {
      return null;
    }
    return new URL(href);
  } catch {
    return null;
  }
};

// Resolved synchronously rather than through evaluateFeatureFlag: this provider
// wraps every page, and the async helpers would add a feature-flag round trip to
// each page load. The query param still takes precedence so the flow can be
// exercised on a deployed environment without an env change.
const isConsentReevaluationEnabled = (): boolean => {
  const override = getBrowserUrl()?.searchParams.get(
    `${FEATURE_FLAG_QUERY_PREFIX}${CONSENT_REEVALUATION_FLAG}`,
  );
  if (override) {
    return override === "true";
  }
  return getEnv(CONSENT_REEVALUATION_FLAG) === "true";
};

//https://github.com/RedVentures/cohesion-client-js/blob/3d1b5ee33e1c8275d2c58977206eda44759a0164/src/libs/monarch/utils.js#L69
export function updatePayloadWithPreviews(payload: Record<string, any>): void {
  const url = new URL(window.location.href);
  const monarchPreviewString = url.searchParams.get("monarchPreview");

  const isStrict = url.searchParams.get("monarchPreviewStrict") === "1";
  const previewTuples = monarchPreviewString
    ? monarchPreviewString.split(";").map((s) => s.split("."))
    : [];
  const previews: { [key: string]: string } = {};
  for (let i = 0; i < previewTuples.length; i++) {
    const previewTuple = previewTuples[i];
    if (i === 0 && previewTuple.length === 1) {
      // Allow the first tuple to implicitly be for the `trafficFlow`.
      previewTuple.unshift("trafficFlow");
    } else if (previewTuple.length !== 2) {
      // Otherwise, require all tuples to have exactly two parts.
      continue;
    }
    const [ruleId, returnValueId] = previewTuple;
    previews[ruleId] = returnValueId;
  }
  if (Object.keys(previews).length !== 0) {
    if (!payload.requestOptions) payload.requestOptions = {};
    if (!payload.requestOptions.forceReturns)
      payload.requestOptions.forceReturns = {};
    payload.requestOptions.forceReturns = {
      ...payload.requestOptions.forceReturns,
      ...previews,
    };
    if (!isStrict) {
      payload.requestOptions.allowInvalidForcedReturns = true;
    }
  }
  const monarchDraftPreviewString = url.searchParams.get("monarchDraft");
  const draftPreviews = monarchDraftPreviewString
    ? monarchDraftPreviewString.split(";")
    : [];
  if (draftPreviews.length !== 0) {
    if (!payload.requestOptions) payload.requestOptions = {};
    if (!payload.requestOptions.draftPreviewIds)
      payload.requestOptions.draftPreviewIds = [];
    payload.requestOptions.draftPreviewIds = [
      ...payload.requestOptions.draftPreviewIds,
      ...draftPreviews,
    ];
  }
}

// Reducer function
const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case "REQUEST_SUCCESS":
      return {
        ...state,
        [action.key]: { isLoading: false, value: action.value },
      };
    case "REQUEST_FAILURE":
      return {
        ...state,
        [action.key]: { isLoading: false, value: undefined },
      };
    default:
      return state;
  }
};

interface MonarchRuleProviderProps {
  children: ReactNode;
}

export const MonarchRuleProvider: React.FC<MonarchRuleProviderProps> = ({
  children,
}) => {
  const locale = useMonarchLocale();
  const [state, dispatch] = useReducer(reducer, {});
  const initialized = useRef(false);
  const queue = useRef<
    Array<{ ruleName: string; params: Record<string, unknown> | undefined }>
  >([]);

  // Swtiched to use ref instead of state to track in-flight requests
  // React's state updates are asynchronous and batched, which can lead to race conditions
  // Multiple components mounting simultaneously need immediate access to the request status
  // The ref provides synchronous access to the current request status
  // This prevents duplicate API calls when many components request the same rule simultaneously
  const inFlightRequests = useRef<Set<string>>(new Set());

  // Every rule that has been evaluated, with whether its latest evaluation
  // carried analytics consent. Rules first evaluated before consent are re-run
  // once consent is granted so their MonarchEvaluated exposure is recorded
  // in-session; without this, a consenting user is only recorded after a manual
  // page refresh.
  const requestedRules = useRef<
    Map<
      string,
      {
        ruleName: string;
        params: Record<string, unknown> | undefined;
        evaluatedWithConsent: boolean;
      }
    >
  >(new Map());
  // Set once analytics consent is granted so an in-flight pre-consent evaluation
  // can re-run itself the moment it settles (see fetchRule's finally block).
  const analyticsConsentGranted = useRef(false);
  const [hasMounted, setHasMounted] = useState(false);
  const [monarchDisabledByQuery, setMonarchDisabledByQuery] = useState(false);

  useEffect(() => {
    const url = getBrowserUrl();
    setMonarchDisabledByQuery(
      url?.searchParams.get("monarchEnabled") === "false",
    );
    setHasMounted(true);
  }, []);

  const processQueue = (
    cb: (params: {
      ruleName: string;
      params: Record<string, unknown> | undefined;
    }) => void,
  ) => {
    const uniqueQueue = Array.from(
      new Set(
        queue.current.map(({ ruleName, params }) =>
          JSON.stringify({ ruleName, params }),
        ),
      ),
    ).map((item) => JSON.parse(item));

    uniqueQueue.forEach(cb);
  };

  useEffect(() => {
    if (initialized.current) return;

    setTimeout(() => {
      if (!initialized.current) {
        processQueue(({ ruleName, params }) => {
          dispatch({
            type: "REQUEST_FAILURE",
            key: JSON.stringify({ ruleName, params }),
          });
        });
      }
    }, 2000);

    cohesion("ready", function () {
      initialized.current = true;
      processQueue(({ ruleName, params }) => {
        fetchRule(ruleName, params);
      });

      queue.current = [];

      // Cohesion is "ready" before the user acts on the consent banner, so the
      // first evaluation of each rule can go out pre-consent and Monarch won't
      // record its MonarchEvaluated exposure. When analytics consent is granted
      // mid-session, replay exactly what a consented refresh runs: re-evaluate
      // every rule that was evaluated without consent. Rules already evaluated
      // with consent are skipped, so an exposure is never double-recorded.
      cohesion("consent:changed", function (groups: string[]) {
        if (!isConsentReevaluationEnabled()) {
          return;
        }
        // The listener fires once with all groups by default before OneTrust is
        // actually ready; wait for OneTrust so the groups are accurate (mirrors
        // CohesionReady).
        if (!window.OneTrust || !window.OnetrustActiveGroups) {
          return;
        }
        // Track the live consent state (not a one-way latch): a later revoke
        // fires consent:changed without C0002 and must reset this, otherwise
        // fetchRule's finally would keep re-running any no-consent evaluation.
        const hasAnalyticsConsentGroup = Boolean(
          groups?.includes(ANALYTICS_CONSENT_GROUP),
        );
        analyticsConsentGranted.current = hasAnalyticsConsentGroup;
        if (!hasAnalyticsConsentGroup) {
          return;
        }

        requestedRules.current.forEach(
          ({ ruleName, params, evaluatedWithConsent }, key) => {
            if (evaluatedWithConsent) {
              return;
            }
            // A still-in-flight pre-consent request re-runs itself when it
            // settles (fetchRule's finally block), avoiding a racing duplicate.
            if (inFlightRequests.current.has(key)) {
              return;
            }
            fetchRule(ruleName, params);
          },
        );
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchRule = async (
    ruleName: string,
    params?: Record<string, unknown> | undefined,
  ) => {
    const key = JSON.stringify({ ruleName, params });

    // Synchronously check if this request is already in flight
    if (inFlightRequests.current.has(key)) {
      return;
    }

    if (!initialized.current) {
      queue.current.push({ ruleName, params });
      return;
    }

    // Synchronously mark this request as in flight
    inFlightRequests.current.add(key);

    // Track the rule and whether this evaluation carries analytics consent, so
    // the consent:changed handler knows which rules still need a consented
    // re-run. Set synchronously (before the await) so a concurrent consent sweep
    // sees an in-progress consented evaluation and skips it.
    requestedRules.current.set(key, {
      ruleName,
      params,
      evaluatedWithConsent: hasAnalyticsConsent(),
    });

    // Clone so updatePayloadWithPreviews cannot mutate the caller's params
    // object (also stored in requestedRules and used to derive `key`).
    const payload = { ...(params ?? {}) } as Record<string, any>;

    try {
      updatePayloadWithPreviews(payload);

      const result = await callMonarchApi(ruleName, payload);

      if (!result) {
        throw new Error("Network response was not ok");
      }

      dispatch({
        type: "REQUEST_SUCCESS",
        key,
        value: withMonarchTranslations(result[ruleName], locale),
      });
    } catch (error) {
      window.newrelic?.noticeError(error as Error, {
        monarchRuleName: ruleName,
      });
      console.error("Failed to fetch Monarch rule:", error);
      dispatch({ type: "REQUEST_FAILURE", key });
    } finally {
      // Always clean up the in-flight request tracking
      inFlightRequests.current.delete(key);

      // If analytics consent landed while this pre-consent evaluation was in
      // flight, re-run it now that it has settled so its exposure is recorded
      // with consent. The re-run marks the rule evaluatedWithConsent, so this
      // resolves after a single extra evaluation.
      if (
        isConsentReevaluationEnabled() &&
        analyticsConsentGranted.current &&
        requestedRules.current.get(key)?.evaluatedWithConsent === false
      ) {
        fetchRule(ruleName, params);
      }
    }
  };

  return (
    <MonarchRuleContext.Provider
      value={{
        state,
        fetchRule,
        hasMounted,
        monarchDisabledByQuery,
      }}
    >
      {children}
    </MonarchRuleContext.Provider>
  );
};

export type UseMonarchRuleOptions = {
  /**
   * When false, the rule is not fetched and the hook returns an idle result.
   * Use to gate Cohesion rules behind feature flags or other conditions.
   */
  enabled?: boolean;
};

export function useMonarchRule<T>(
  ruleName: string,
  params?: Record<string, unknown>,
  options?: UseMonarchRuleOptions,
): RuleState<T> {
  const context = useContext(MonarchRuleContext);
  if (!context) {
    throw new Error("useMonarchRule must be used within a MonarchRuleProvider");
  }

  const { state, fetchRule, hasMounted, monarchDisabledByQuery } = context;
  const key = JSON.stringify({ ruleName, params });
  const enabled = options?.enabled !== false;

  useEffect(() => {
    if (!enabled || !hasMounted) {
      return;
    }
    if (monarchDisabledByQuery) {
      return;
    }
    if (!state[key]) {
      fetchRule(ruleName, params);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    key,
    ruleName,
    params,
    state,
    enabled,
    hasMounted,
    monarchDisabledByQuery,
  ]);

  if (!enabled || (hasMounted && monarchDisabledByQuery)) {
    return {
      isLoading: false,
      value: undefined as unknown as T,
    };
  }

  return (
    (state[key] as unknown as RuleState<T>) ?? {
      isLoading: true,
      value: undefined,
    }
  );
}
